import '.a{display:flex;}.ssr-plugin'
import '.ac{flex-direction:column;}.ssr-plugin'
import '.bk{gap:8px;}.ssr-plugin'
import '.f{width:100%;}.ssr-plugin'
import '.c7{padding-bottom:8px;}.ssr-plugin'
import '.ag{justify-content:space-between;}.ssr-plugin'
import '.f1{padding:8px 8px 8px 14px;}.ssr-plugin'
import '.l{gap:12px;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import '.a7{position:relative;}.ssr-plugin'
import '.f2{min-height:36px;}.ssr-plugin'
import '.f3{border:1px solid transparent;}.ssr-plugin'
import '.f4{padding-top:7px;}.ssr-plugin'
import '.f5{border:0px solid yellow;}.ssr-plugin'
import '.f6{font-size:13px;}.ssr-plugin'
import '.f7{font-family:Geist Mono;}.ssr-plugin'
import '.f8{line-height:20px;}.ssr-plugin'
import '.f9{counter-increment:line;}.ssr-plugin'
import '.an{position:absolute;}.ssr-plugin'
import '.bb{inset:0px;}.ssr-plugin'
import '.fa{gap:6px;}.ssr-plugin'
import { Highlight, themes } from 'prism-react-renderer'
import { Style, styled } from 'inlines'
import { colors } from '../../tokens/colors.js'
import { radius } from '../../utils/radius.js'
import { Tooltip } from '../Tooltip/index.js'
import { useTheme } from '../../hooks/useTheme.js'
import { useToast } from '../Toast/index.js'
import { useEffect, useState } from 'react'
import { TextAreaInput } from '../TextAreaInput/index.js'
import { Text } from '../Text/index.js'
import { Icon } from '../Icon/index.js'
import { useRef } from 'react'
import { IconButton } from '../IconButton/index.js'
import { Stack } from '../Stack/index.js'
import { Separator } from '../Separator/index.js'

type CodeInputProps = {
  value?: string
  onChange: (value: string) => void
  language:
    | 'js'
    | 'jsx'
    | 'ts'
    | 'tsx'
    | 'bash'
    | 'rust'
    | 'python'
    | 'json'
    | 'markdown'
    | 'css'
  lineNumbers?: boolean
  border?: boolean
  error?: string
  placeholder?: string
  disabled?: boolean
  rows?: 'adaptive' | number
  label?: string
  suffix?: string | React.ReactNode
  description?: string
  maxHeight?: number
  style?: Style
}

function CodeInput({
  language = 'ts',
  value = '',
  onChange,
  lineNumbers = true,
  border = true,
  error,
  placeholder,
  disabled,
  rows,
  label,
  description,
  maxHeight,
  suffix,
  style,
}: CodeInputProps) {
  const { theme } = useTheme()
  const toast = useToast()
  const [focus, setFocus] = useState(false)
  const [overflow, setOverflow] = useState(false)
  const [textAreaHeight, setTextAreaHeight] = useState<number | undefined>(
    undefined,
  )

  const textAreaWrapperRef = useRef<HTMLDivElement>(null)
  const highlightRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (textAreaWrapperRef.current) {
      if (
        (textAreaWrapperRef.current.firstChild as HTMLElement).clientHeight >
        maxHeight
      ) {
        setOverflow(true)
      } else {
        setOverflow(false)
      }
    }

    if (textAreaWrapperRef.current && highlightRef.current) {
      // Force layout calculation in Safari
      const height = highlightRef.current.offsetHeight
      // 14 for padding top offset
      setTextAreaHeight(height + 16)
    }
  }, [value])

  const typeOfRowsIsNumber = typeof rows === 'number'
  const linesNumber = typeOfRowsIsNumber
    ? rows
    : value
      ? value.split('\n')?.length
      : 0
  const linesPlural = linesNumber === 1 ? 'line' : 'lines'

  return (
    <div className="a ac bk f"
    >
      {label && (
        <Text
          variant="subtext-regular"
          color={focus ? 'neutral100' : 'neutral60'}
        >
          {label}
        </Text>
      )}

      <styled.div className="c7"
        style={{
          borderRadius: radius[12],
          background: focus ? colors.neutralInverted100 : 'transparent',
          ...(border && {
            border: `1px solid ${focus ? colors.neutral100 : disabled ? 'transparent' : colors.neutral20Adjusted}`,
            outline: focus
              ? `4px solid ${colors.neutral20}`
              : '4px solid transparent',
            overflowX: 'hidden',
          }),
          ...(disabled && {
            cursor: 'not-allowed',
            pointerEvents: 'none',
            background: colors.neutral5,
          }),
          ...(error && {
            border: `1px solid ${colors.red80}`,
            ...(focus &&
              !disabled && {
                background: colors.neutralInverted100,
                border: `1px solid ${colors.red100}`,
                outline: `4px solid ${colors.red60}`,
              }),
          }),
        }}
      >
        <div className="a ag f1 l d"
          style={{
            borderBottom: disabled
              ? `1px solid ${colors.neutral5}`
              : `1px solid ${colors.neutral20Adjusted}`,
          }}
        >
          <Stack>
            <Text
              variant="subtext-mono"
              style={{ color: disabled ? colors.neutral20 : colors.neutral60 }}
            >
              {language.toUpperCase()}
            </Text>

            {suffix && (
              <>
                <Separator variant="dot" />
                <Text
                  variant="subtext-mono"
                  style={{
                    color: disabled ? colors.neutral20 : colors.neutral60,
                  }}
                >
                  {suffix}
                </Text>
              </>
            )}
          </Stack>

          <div className="a d l"
          >
            {value && (
              <Text
                variant="subtext-mono"
                style={{
                  color: disabled ? colors.neutral20 : colors.neutral80,
                }}
              >
                {linesNumber} {linesPlural}
              </Text>
            )}
            <Tooltip description="Copy code">
              <IconButton
                icon="copy"
                size="small"
                disabled={disabled}
                onClick={() => {
                  navigator.clipboard.writeText(value)
                  toast('Code copied')
                }}
              />
            </Tooltip>
          </div>
        </div>

        <styled.div className="a7 f f2 f3 f4"
          style={{
            overflowY: !overflow ? 'hidden' : 'scroll',
            height: typeOfRowsIsNumber ? 26.83 * rows + 'px' : 'auto',
            maxHeight: maxHeight || 520,
            borderBottomLeftRadius: radius[12],
            ...(disabled && {
              cursor: 'not-allowed',
            }),
            ...style,
          }}
        >
          <div className="f5" ref={highlightRef}>
            <Highlight
              theme={theme === 'light' ? themes.oneLight : themes.oneDark}
              code={value}
              language={language}
            >
              {({ tokens, getTokenProps }) => (
                <pre
                  style={{
                    padding: '8px 0px',
                    overflow: 'hidden',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {tokens.map((line, i) => {
                    return (
                      <styled.div className="a7 f6 f7 f8 f9"
                        key={i}
                        style={{
                          padding: lineNumbers ? '0px 16px' : '0 14px',
                          opacity: disabled ? 0.4 : 1,
                          ...(lineNumbers && {
                            '&:before': {
                              display: 'inline-block',
                              minWidth: 32,
                              textAlign: 'left',
                              marginRight: 8,
                              content: 'counter(line)',
                              color: disabled
                                ? colors.neutral20
                                : colors.neutral60,
                            },
                          }),
                        }}
                      >
                        {line.map((token, ii) => (
                          <span key={ii} {...getTokenProps({ token })} />
                        ))}
                      </styled.div>
                    )
                  })}
                </pre>
              )}
            </Highlight>
          </div>

          <div className="an bb"
            ref={textAreaWrapperRef}
          >
            <TextAreaInput
              variant="ghost"
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              onFocus={() => {
                setFocus(true)
              }}
              onBlur={() => {
                setFocus(false)
              }}
              style={{
                border: '0px solid red',
                fontSize: 13,
                fontFamily: 'Geist Mono',
                lineHeight: '20px',
                letterSpacing: 'normal',
                color: 'rgba(0,0,0,0)',
                // color:'red',
                caretColor: colors.neutral100,
                backgroundColor: 'transparent',
                paddingTop: 15,
                paddingLeft: lineNumbers ? 56 : 13,
                overflow: 'hidden',
                height: textAreaHeight || '100%',
                minHeight: textAreaHeight || 34,
                resize: 'none',
              }}
            />
          </div>
        </styled.div>
      </styled.div>
      {error && (
        <div className="a fa d"
          style={{
            color: colors.red100,
          }}
        >
          <Icon variant="error-filled" color="inherit" />
          <Text variant="display-medium" color="red100">
            {error}
          </Text>
        </div>
      )}
      {description && (
        <Text variant="display-regular" color="neutral60">
          {description}
        </Text>
      )}
    </div>
  )
}

export { CodeInput }
export type { CodeInputProps }
